import React from 'react'
import styled from 'styled-components'
import { PageContent, PageHeader } from '../../../common'
import { Colors } from '../../../constants'
import { usePositions } from './usePositions'
import { ClaimButton } from './claim/ClaimButton'
import { DepositButton } from './deposit/DepositButton'
import { WithdrawButton } from './withdraw/WithdrawButton'
import { StakePositions } from './StakePositions'
import { getStakingParams, StakingOption } from './utils'

const COLUMNS_NUMBER = 3

export function Stake() {
  return (
    <PageContentStyled>
      <PageHeader label="Stake ITGR" />
      <Table>
        <thead>
          <Row>
            <th scope="col">Duration</th>
            <th scope="col">APR</th>
            <th scope="col">Actions</th>
          </Row>
        </thead>
        <tbody>
          <SingleStake stakingOption={StakingOption.THREE_YEARS} />
          <SingleStake stakingOption={StakingOption.SIX_MONTHS} />
          <SingleStake stakingOption={StakingOption.DEPRECATED_THREE_YEARS} />
          <SingleStake stakingOption={StakingOption.DEPRECATED_SIX_MONTHS} />
        </tbody>
      </Table>
    </PageContentStyled>
  )
}

interface SingleStakeProps {
  stakingOption: StakingOption
}

export function SingleStake({ stakingOption }: SingleStakeProps) {
  const { canStake, positions, withdrawable, claimable } = usePositions(stakingOption)
  const { duration, apr } = getStakingParams(stakingOption)

  // Don't show if user doesn't have claimable or withdrawable funds from deprecated contracts
  if (
    [StakingOption.DEPRECATED_SIX_MONTHS, StakingOption.DEPRECATED_THREE_YEARS].includes(stakingOption) &&
    !claimable?.value.gt(0) &&
    !withdrawable?.value.gt(0)
  ) {
    return <></>
  }

  return (
    <>
      <Row>
        <td>
          <Label>{duration}</Label>
        </td>
        <td>{apr}</td>
        <td>
          <Actions>
            {canStake && <DepositButtonStyled stakingOption={stakingOption} />}
            {withdrawable?.value.gt(0) && <WithdrawButtonStyled stakingOption={stakingOption} />}
            <ClaimButton stakingOption={stakingOption} />
          </Actions>
        </td>
      </Row>
      <DarkRow>
        <td colSpan={COLUMNS_NUMBER}>
          <StakePositions positions={positions} />
        </td>
      </DarkRow>
    </>
  )
}

const PageContentStyled = styled(PageContent)`
  margin: 0 auto 0 15px;
  background: rgb(38, 41, 44);
  background: linear-gradient(0deg, rgba(38, 41, 44, 1) 0%, rgba(52, 57, 68, 1) 100%);
  border: 1px solid ${Colors.DarkGray};

  @media (max-width: 1024px) {
    margin: 30px 15px 0px 15px;
  }
`

const Table = styled.table`
  width: 100%;
  border-spacing: 0;
  overflow-x: auto;

  th,
  td {
    margin: 0;
    padding: 15px 15px;
    font-size: 12px;
    color: ${Colors.White};

    &:first-child {
      text-align: left;
    }

    &:last-child {
      text-align: right;
    }
  }

  th {
    width: 33%;
    background-color: #272b32;
    border: 1px solid #272b32;
    text-transform: uppercase;
  }

  td {
    text-align: center;
  }
`

const Row = styled.tr`
  td {
    &:last-child {
      padding: 10px 15px;
    }
  }
`

const DarkRow = styled.tr`
  background-color: rgba(0, 0, 0, 0.1);
`

const Label = styled.span`
  white-space: nowrap;
`

const Actions = styled.div`
  display: flex;
`

const DepositButtonStyled = styled(DepositButton)`
  margin-right: 10px;
`

const WithdrawButtonStyled = styled(WithdrawButton)`
  margin-right: 10px;
`
